import { keyframes, styled } from "styled-components";
import { transition } from "../styles/mixins";
import { useEffect, useState } from "react";
import Typography from "./common/Typography";

const animation = keyframes`
  from {
    rotate: 0deg;
  }

  to {
    rotate: 360deg;
  }
`;

const animationBackground = keyframes`
  from {
    scale: 1;
  }

  to {
    scale: 1.25;
  }
`;

const Root = styled.div`
  ${transition('opacity translate')}
  align-items: center;
  background-size: cover;
  display: flex;
  inset: 0;
  justify-content: center;
  opacity: ${(props) => props.$isLoading ? '1' : '0'};
  overflow: hidden;
  pointer-events: none;
  position: fixed;
  translate: ${(props) => props.$isLoading ? '0' : '100%'};
  z-index: 2;

  &::after,
  &::before {
    --width: 96px;
    border: var(--border-width) var(--border-style) var(--color-theme-secondary-400);
    content: '';
    height: calc(100% - (var(--width) / 2));
    position: absolute;
    top: 50%;
    translate: 0 -50%;
    width: var(--width);
    z-index: 3;
  }

  &::before {
    border-right: 0;
    left: 24px;
  }

  &::after {
    border-left: 0;
    right: 24px;
  }
`;

const Background = styled.div`
  animation-duration: 15000ms;
  animation-fill-mode: forwards;
  animation-name: ${animationBackground};
  background-color: var(--color-theme-primary-500);
  background-image: url('./images/background.png');
  inset: 0;
  position: absolute;
`;

const LoadingText = styled(Typography)`
  position: absolute;
  top: 50%;
  left: 50%;
  translate: -50% -50%;
  z-index: 1;
`;

const Star = styled.img`
  animation-duration: 5000ms;
  animation-iteration-count: infinite;
  animation-name: ${animation};
  animation-timing-function: ease-out;
  max-width: 420px;
`;

const Version = styled.div`
  bottom: var(--space-8);
  font-size: var(--font-size-sm);
  left: var(--space-8);
  position: absolute;
`;

const Tips = styled.div`
  background-color: rgba(var(--color-theme-primary-400-rgb), 0.5);
  bottom: var(--space-6);
  font-size: 14px;
  left: 50%;
  padding: var(--space-3);
  padding: var(--space-3);
  position: absolute;
  text-align: center;
  translate: -50%;
  
  &::after,
  &::before {
    aspect-ratio: 1;
    content: '';
    height: 50%;
    position: absolute;
  }

  &::after {
    border-bottom: var(--border-width) var(--border-style) var(--color-theme-accent-400);
    border-right: var(--border-width) var(--border-style) var(--color-theme-accent-400);
    bottom: 0;
    right: 0;
  }

  &::before {
    border-left: var(--border-width) var(--border-style) var(--color-theme-accent-400);
    border-top: var(--border-width) var(--border-style) var(--color-theme-accent-400);
    left: 0;
    top: 0;
  }
`;

function Loading({ store }) {
  const loading = store.state.loading;
  const tips = [
    "Engage in Trading: Take advantage of the vast trade networks in space to buy and sell resources, upgrade your ship, and make a profit. Stay informed about market trends and make smart trading decisions to maximize your earnings.",
    "Explore and Discover: In a vast and unknown universe, take the time to explore every nook and cranny. You never know what secrets, resources, or breathtaking sights you might stumble upon.",
    "Have Fun and Immerse Yourself: Above all, remember that this is a game meant to be enjoyed. Immerse yourself in the vastness of space, embrace the unknown, and have fun exploring the wonders that await you. May your journey be filled with thrilling adventures and unforgettable moments!",
    "Join or Form Alliances: Space can be a dangerous place, so consider joining or forming alliances with other players. Together, you can tackle more significant challenges, share resources, and explore new territories as a united force.", 
    "Manage Resources: Keep a close eye on your resources, such as fuel, ammunition, and supplies. Plan your journeys wisely and make strategic decisions to ensure you have enough to sustain yourself during long expeditions or intense battles.",
    "Master the Controls: Space navigation can be tricky, so take some time to master the controls. Familiarize yourself with maneuvering, docking, and combat techniques to become a skilled pilot and navigate the cosmos with ease.",
    "Upgrade and Customize: Enhance your spaceship with upgrades to improve its performance and capabilities. Tailor your ship's weapons, shields, and engines to suit your playstyle and stay ahead of the challenges that lie ahead.",
  ];

  const randomTips = () => {
    return tips[(Math.random() * tips.length) | 0];
  }

  const [tip, setTip] = useState(randomTips());

  useEffect(() => {
    const interval = setInterval(() => {
      setTip(randomTips);
    }, 5000);

    return () => clearInterval(interval);
  });

  return (
    <Root $isLoading={loading}>
      <Background />
      <LoadingText as="h1" fontSize="6xl">Loading</LoadingText>
      <Star src="./images/waypoint/types/PLANET_1.png" />
      <Version>2.0 (alpha)</Version>
      <Tips>
        <Typography as="h2">
            {tip}
        </Typography>
      </Tips>
    </Root>
  )
}

export default Loading;